'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import { useLocale } from 'next-intl';
import { Button } from '@/components/ui/button';
import { getAppUpdateCopy } from '@/i18n/app-update-copy';
import {
  useReloadBlocker,
  useReloadSafety,
} from '@/contexts/reload-safety-context';
import { getReloadSafety } from '@/lib/reload-blockers';
import {
  createWebBuildPoller,
  fetchWebBuildIdentity,
  LOADED_WEB_BUILD_ID,
  tryReloadForBuild,
  WEB_BUILD_POLL_INTERVAL_MS,
} from '@/lib/web-build-identity';
import { pendingSendStore } from '@/components/work-chats/pending-send-store';

const hasUnfinishedSend = () =>
  pendingSendStore.getSnapshot().some((item) => item.status !== 'sent');

export function WebBuildUpdateController() {
  const copy = getAppUpdateCopy(useLocale());
  const [latest, setLatest] = useState<string | null>(null);
  const safety = useReloadSafety();
  const pendingSends = useSyncExternalStore(
    pendingSendStore.subscribe,
    hasUnfinishedSend,
    () => false
  );
  useReloadBlocker(
    'work-chat-pending-sends',
    pendingSends ? 'blocked' : 'safe'
  );
  useEffect(() => {
    const poller = createWebBuildPoller({
      fetchIdentity: fetchWebBuildIdentity,
      onIdentity: setLatest,
    });
    const pollVisible = () => {
      if (document.visibilityState === 'visible') void poller.poll();
    };
    void poller.poll();
    window.addEventListener('focus', pollVisible);
    document.addEventListener('visibilitychange', pollVisible);
    const interval = setInterval(pollVisible, WEB_BUILD_POLL_INTERVAL_MS);
    return () => {
      poller.dispose();
      clearInterval(interval);
      window.removeEventListener('focus', pollVisible);
      document.removeEventListener('visibilitychange', pollVisible);
    };
  }, []);
  useEffect(() => {
    try {
      tryReloadForBuild({
        loaded: LOADED_WEB_BUILD_ID,
        latest,
        safety: getReloadSafety(),
        storage: window.sessionStorage,
        reload: () => window.location.reload(),
      });
    } catch {
      /* Storage access can itself throw. Keep the manual action. */
    }
  }, [latest, safety]);
  if (!latest || !LOADED_WEB_BUILD_ID || latest === LOADED_WEB_BUILD_ID)
    return null;
  return (
    <div
      role="status"
      data-web-build-update="true"
      className="bg-background border-border shadow-popover fixed end-4 bottom-4 z-50 flex max-w-sm items-center gap-2 rounded-xl border p-3"
    >
      <p className="text-muted-foreground text-sm">
        {safety === 'blocked' ? copy.blocked : copy.description}
      </p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => window.location.reload()}
      >
        {copy.reload}
      </Button>
    </div>
  );
}
